import React from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '../hooks';
import { statsData, awardsData, companyInfo } from '../data/siteData';
import { PartnersSection, NewsletterSection } from '../components/sections';

const AboutPage = () => {
  const [missionRef, missionInView] = useInView(0.1);
  const [awardsRef, awardsInView] = useInView(0.1);

  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-16 md:py-24 relative african-pattern">
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <p className="section-subtitle text-primary-500">WHO WE ARE</p>
          <h1 className="section-title mb-6">
            ABOUT <span className="text-gradient">RIVEN BYTE</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            We are a Tanzanian game development studio on a mission to bring
            African stories, cultures, and adventures to gamers worldwide.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div
            ref={missionRef}
            className={`grid lg:grid-cols-2 gap-16 items-center transition-all duration-1000 ${
              missionInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Mission</h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                At Riven Byte, we believe that gaming is a universal language that
                transcends borders. Our mission is to create world-class gaming
                experiences that celebrate African heritage while pushing the
                boundaries of interactive entertainment.
              </p>
              <p className="text-gray-400 text-lg leading-relaxed">
                We're not just making games—we're preserving stories, honoring
                traditions, and inspiring the next generation of African game
                developers to dream big and build boldly.
              </p>
            </div>

            <div className="relative">
              <div className="aspect-square rounded-3xl bg-gradient-to-br from-primary-500/20 to-secondary-400/20 p-8 border border-white/10">
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-40 h-40 mx-auto mb-6 rounded-full bg-gradient-to-br from-primary-500 to-secondary-400 flex items-center justify-center animate-float">
                      <span className="font-gaming text-6xl font-black">RB</span>
                    </div>
                    <p className="text-xl font-gaming text-primary-500">
                      SINCE {companyInfo.founded}
                    </p>
                    <p className="text-gray-400 mt-2">{companyInfo.location}</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary-500/30 rounded-full blur-2xl animate-pulse" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-secondary-400/30 rounded-full blur-2xl animate-pulse" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {statsData.map((stat) => (
              <div key={stat.id} className="text-center">
                <div className="font-gaming text-4xl md:text-5xl text-primary-500 mb-2">
                  {stat.value}
                  {stat.suffix}
                </div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="section-subtitle text-secondary-400">WHAT DRIVES US</p>
            <h2 className="section-title">
              OUR <span className="text-gradient">VALUES</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: '🌍',
                title: 'African Pride',
                description:
                  'We celebrate our heritage and showcase the richness of African cultures through every game we create.',
              },
              {
                icon: '🎮',
                title: 'Quality First',
                description:
                  'We never compromise on quality. Every game meets international standards while staying true to our roots.',
              },
              {
                icon: '🤝',
                title: 'Community',
                description:
                  'Our players are family. We build games with our community, not just for them.',
              },
              {
                icon: '🚀',
                title: 'Innovation',
                description:
                  'We push boundaries and embrace new technologies to create unique gaming experiences.',
              },
            ].map((value, index) => (
              <div
                key={value.title}
                className="card-hover p-8 bg-white/5 rounded-3xl border border-white/10 text-center"
              >
                <div className="text-5xl mb-4">{value.icon}</div>
                <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                <p className="text-gray-400">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team CTA */}
      <section className="py-16 bg-gradient-to-b from-transparent via-secondary-400/5 to-transparent">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="section-subtitle text-secondary-400">THE DREAMERS</p>
          <h2 className="section-title mb-4">
            MEET THE <span className="text-gradient">TEAM</span>
          </h2>
          <p className="text-gray-400 mb-8">
            Learn about the passionate people behind Riven Byte's African gaming revolution.
          </p>
          <Link to="/team" className="btn-primary inline-block">Meet the Full Team →</Link>
        </div>
      </section>

      {/* Awards */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="section-subtitle text-primary-500">RECOGNITION</p>
            <h2 className="section-title">
              AWARDS & <span className="text-gradient">ACHIEVEMENTS</span>
            </h2>
          </div>

          <div
            ref={awardsRef}
            className={`grid md:grid-cols-3 gap-8 transition-all duration-1000 ${
              awardsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            {awardsData.map((award) => (
              <div
                key={award.id}
                className="card-hover p-8 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-3xl border border-yellow-500/20 text-center"
              >
                <div className="text-5xl mb-4">🏆</div>
                <h3 className="text-xl font-bold mb-2">{award.title}</h3>
                <p className="text-yellow-400 font-medium mb-2">{award.event}</p>
                <p className="text-gray-400 text-sm">
                  {award.game} • {award.year}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-white/5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="section-subtitle text-primary-500">OUR JOURNEY</p>
            <h2 className="section-title">
              THE <span className="text-gradient">TIMELINE</span>
            </h2>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-500 to-secondary-400" />

            {[
              {
                year: '2024',
                title: 'Riven Byte Founded',
                description: 'Started in Dar es Salaam with a team of 5 passionate developers',
              },
              {
                year: '2024',
                title: 'First Game Release',
                description: 'Maasai Runners launched to critical acclaim',
              },
              {
                year: '2024',
                title: 'Team Expansion',
                description: 'Grew to 50+ team members across multiple departments',
              },
              {
                year: '2025',
                title: 'Global Recognition',
                description: 'Won Best African Game at international awards',
              },
              {
                year: '2025',
                title: 'Console Debut',
                description: 'First titles released on PlayStation and Xbox',
              },
            ].map((item, index) => (
              <div
                key={index}
                className={`relative flex items-center gap-8 mb-12 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                <div className="hidden md:block flex-1" />
                <div className="absolute left-4 md:left-1/2 w-4 h-4 -ml-2 rounded-full bg-primary-500 border-4 border-black z-10" />
                <div className="ml-12 md:ml-0 flex-1 p-6 bg-white/5 rounded-2xl border border-white/10">
                  <span className="text-primary-500 font-gaming">{item.year}</span>
                  <h3 className="text-xl font-bold mt-1">{item.title}</h3>
                  <p className="text-gray-400 mt-2">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PartnersSection />
      <NewsletterSection />
    </div>
  );
};

export default AboutPage;
